import store from "@/store";
import DcbService from "@/services/dcbService";
import {getBrandConfig} from "@/brand";
import {GameToBrandMap, brandConfigObjectMap} from "@/brand/brandConfig";

function getLiveBrandConfig() {
    const candidates = [store.state.brandConfig?.productId, store.state.productId];
    let pid = null;
    for (const c of candidates) {
        const n = parseInt(c, 10);
        if (Number.isFinite(n) && n > 0) { pid = n; break; }
    }
    if (!pid) return store.state.brandConfig || {};
    const brandKey = GameToBrandMap.get(pid);
    return brandConfigObjectMap.get(brandKey) || store.state.brandConfig || {};
}

function redirectToExternalPlatform(baseUrl) {
    const urlParams = new URLSearchParams(window.location.search);
    const eMsisdn = urlParams.get('e-msisdn');
    const rawMsisdn = store.state.user?.msisdn || store.state.msisdn;
    let url = String(baseUrl || '').replace(/\/+$/, '');
    const param = eMsisdn ? `e-msisdn=${encodeURIComponent(eMsisdn)}`
        : rawMsisdn ? `msisdn=${encodeURIComponent(rawMsisdn)}`
        : '';
    if (param) {
        const sep = url.includes('?') ? '&' : '?';
        url = `${url}${sep}${param}`;
    }
    window.location.replace(url);
}

export async function userAuthenticate(to, from, next) {
    const urlParams = new URLSearchParams(window.location.search);
    const productId = urlParams.get('productId');
    const storedProductId = sessionStorage.getItem('productId');

    if (productId && productId !== store.state.productId) {
        store.commit('setProductId', productId);
        const brandId = GameToBrandMap.get(Number(productId));
        const brandConfig = brandId ? getBrandConfig(brandId) : getBrandConfig(5);
        store.commit('setBrandConfig', brandConfig);
    } else if (!productId && (storedProductId || store.state.productId) && !store.state.brandConfig) {
        const currentProductId = storedProductId || store.state.productId;
        const brandId = GameToBrandMap.get(Number(currentProductId));
        const brandConfig = brandId ? getBrandConfig(brandId) : getBrandConfig(5);
        store.commit('setProductId', currentProductId);
        store.commit('setBrandConfig', brandConfig);
    }

    const encryptedMsisdn = urlParams.get('e-msisdn');
    const liveBrand = getLiveBrandConfig();
    const disallowed = liveBrand.disallowedRoutes || [];
    const allowed = liveBrand.allowedRoutes || null;
    const defaultRoute = liveBrand.defaultRoute || 'SelectVoucher';

    if (liveBrand.skipOtpAuth) {
        if (encryptedMsisdn && !store.state.user) {
            return await encryptedMsisdnAuth(encryptedMsisdn, next, () => {
                if (liveBrand.externalRedirectUrl) {
                    redirectToExternalPlatform(liveBrand.externalRedirectUrl);
                    return next(false);
                }
                return next({name: defaultRoute});
            });
        }
        if (disallowed.includes(to.name) || (allowed && !allowed.includes(to.name))) {
            if (store.state.user) {
                return next({name: defaultRoute});
            }
            if (liveBrand.externalRedirectUrl) {
                redirectToExternalPlatform(liveBrand.externalRedirectUrl);
                return next(false);
            }
            return next({name: defaultRoute});
        }
        return next();
    }

    if (disallowed.includes(to.name) || (allowed && !allowed.includes(to.name))) {
        if (liveBrand.externalRedirectUrl) {
            redirectToExternalPlatform(liveBrand.externalRedirectUrl);
            return next(false);
        }
        return next({name: defaultRoute});
    }

    if (to.name === 'SelectVoucher' && store.state.user) {
        return next();
    }

    const otpAllowedRoutes = ['RequestOtp'];

    if (encryptedMsisdn) return await encryptedMsisdnAuth(encryptedMsisdn, next);

    if (store.state.msisdn) return next();

    if (otpAllowedRoutes.includes(to.name)) {
        return next();
    }

    if (!otpAllowedRoutes.includes(to.name) && store.state.msisdn === undefined) {
        return next({name: 'RequestOtp'});
    }
}

async function encryptedMsisdnAuth(encryptedMsisdn, next, onFailure) {
    try {
        const decryptMsisdnResponse = (await DcbService.decryptMsisdn(encryptedMsisdn)).data;
        const userDataResponse = await getUser(decryptMsisdnResponse.rawMsisdn);
        await setUserData(userDataResponse);

        store.commit('setMsisdn', decryptMsisdnResponse.rawMsisdn);

        return redirectToSelectVoucher(store.state.user, next);
    } catch (error) {
        console.error("Authentication error:", error);
        if (typeof onFailure === 'function') return onFailure();
        return next({name: 'RequestOtp'});
    }
}

async function getUser(msisdn) {
    const {data: userResponse} = await DcbService.getUserViaProductId(msisdn, store.state.productId);
    return userResponse;
}

async function setUserData(userDataResponse) {
    store.commit('setUser', userDataResponse);
}

function redirectToSelectVoucher(userStatus, next) {
    return next({name: 'SelectVoucher'});
}
